import { ff } from '@atlassian/jira-feature-flagging';
import { fetchJson, performPostRequest } from '@atlassian/jira-fetch';
import type { useIntl } from '@atlassian/jira-intl';
import {
	isSynthetic,
	X2C_SERVICE_URL_PREFIX,
} from '@atlassian/jira-project-create-importer-common';
import type {
	ThirdPartyEntityPage,
	ProjectData,
	AccessTokenStatus,
	StartThirdPartyImportPayload,
	ThirdPartyEntity,
	ThirdPartyEntityStats,
	ThirdPartyMembers,
	ThirdPartyEntityStatuses,
} from '../../../types';

import type { ThirdPartyClient, ThirdPartyFieldConfigData } from '../types';
import { handleAuthenticationFactory, transformToFolderAndSubfolderState } from '../utils';
import messages from './messages';
import { transformToMondaySiteName } from './utils';

const BACKEND_MAIN_WORKSPACE_ID = '-1';
const MAX_RETRIES = 1;

interface MondayClientConstructorParams {
	cloudId: string;
	formatMessage: ReturnType<typeof useIntl>['formatMessage'];
	requestAuthenticationToken?: () => Promise<boolean>;
}

export class MondayClient implements ThirdPartyClient {
	private readonly BASE_URL: string;

	private readonly BASE_HEADERS: Record<string, string> = {
		'x-issynthetic-traffic': String(isSynthetic()),
		'x-migration-authentication-type': 'personal-access-token',
	};

	private readonly formatMessage: ReturnType<typeof useIntl>['formatMessage'];

	private readonly withAuthentication: ReturnType<typeof handleAuthenticationFactory>;

	constructor({
		cloudId,
		formatMessage,
		requestAuthenticationToken,
	}: MondayClientConstructorParams) {
		this.BASE_URL = `${X2C_SERVICE_URL_PREFIX}/${cloudId}/import/monday`;
		this.formatMessage = formatMessage;
		this.withAuthentication = requestAuthenticationToken
			? handleAuthenticationFactory({
					retries: MAX_RETRIES,
					requestAuthenticationToken,
				})
			: <T,>(doRequest: () => Promise<T>): Promise<T> => doRequest();
	}

	startImport({
		entityId,
		payload,
	}: {
		entityId: string;
		payload: StartThirdPartyImportPayload;
	}): Promise<{ migrationId: string }> {
		const url = `${this.BASE_URL}/board/${entityId}/migration/start`;
		return performPostRequest(url, {
			body: JSON.stringify(payload),
			headers: {
				...this.BASE_HEADERS,
				'Content-Type': 'application/json',
			},
		});
	}

	async getEntities({ workspaceId, folderIds }: { workspaceId: string; folderIds?: string[] }) {
		let url = `${this.BASE_URL}/workspaces/${workspaceId}/boards`;
		if (folderIds) {
			url = `${url}?folderIds=${folderIds}`;
		}
		const entities = await this.withAuthentication(() =>
			fetchJson<ThirdPartyEntity[]>(url, {
				method: 'GET',
				headers: this.BASE_HEADERS,
			}),
		);

		return entities.map((workspace) => {
			if (workspace.id === BACKEND_MAIN_WORKSPACE_ID) {
				return {
					...workspace,
					name: this.formatMessage(messages.mainWorkspace),
				};
			}
			return workspace;
		});
	}

	getEntityStats({ entityId }: { entityId: string }): Promise<ThirdPartyEntityStats> {
		const url = `${this.BASE_URL}/board/${entityId}/stats`;

		return this.withAuthentication(() =>
			fetchJson<ThirdPartyEntityStats>(url, {
				method: 'GET',
				headers: {
					...this.BASE_HEADERS,
				},
			}),
		);
	}

	getWorkspaces(): Promise<ThirdPartyEntity[]> {
		const url = `${this.BASE_URL}/workspaces`;

		return this.withAuthentication(() =>
			fetchJson<ThirdPartyEntity[]>(url, {
				method: 'GET',
				headers: this.BASE_HEADERS,
			}),
		);
	}

	getEntityMembers(): Promise<ThirdPartyMembers[]> {
		return Promise.reject(new Error('getEntityMembers method is not implemented in Monday'));
	}

	async getFolders({ entityId }: { entityId: string }) {
		const url = `${this.BASE_URL}/workspaces/${entityId}/folders`;

		const folders = await this.withAuthentication(() =>
			fetchJson(url, {
				method: 'GET',
				headers: this.BASE_HEADERS,
			}),
		);
		const data = transformToFolderAndSubfolderState(folders);
		return data;
	}

	getFieldConfig({
		entityId,
		entities,
		projectData,
	}: {
		entityId: string;
		entities: ThirdPartyEntity[];
		projectData: ProjectData;
	}): Promise<ThirdPartyFieldConfigData> {
		if (entities.length === 0 && ff('x2c-clickup-importer-multi-list')) {
			return Promise.reject(new Error('No entities provided'));
		}

		const url = ff('x2c-clickup-importer-multi-list')
			? `${this.BASE_URL}/board/${entities[0].id}/fields`
			: `${this.BASE_URL}/board/${entityId}/fields`;

		return this.withAuthentication(() =>
			fetchJson<ThirdPartyFieldConfigData>(url, {
				method: 'POST',
				body: JSON.stringify({
					projectDetails: {
						type: projectData.type,
						style: projectData.style,
					},
				}),
				headers: this.BASE_HEADERS,
			}),
		);
	}

	validateAccessToken(): Promise<{ status: AccessTokenStatus }> {
		const url = `${this.BASE_URL}/token/status`;
		return fetchJson<{ status: AccessTokenStatus }>(url, {
			method: 'GET',
			headers: this.BASE_HEADERS,
		});
	}

	async getSiteName(): Promise<string> {
		const url = `${this.BASE_URL}/account`;

		const data = await this.withAuthentication(() =>
			fetchJson<{ slug: string }>(url, {
				method: 'GET',
				headers: this.BASE_HEADERS,
			}),
		);

		return transformToMondaySiteName(data);
	}

	postAccessToken({ accessToken }: { accessToken: string }): Promise<void> {
		const url = `${this.BASE_URL}/token`;
		return performPostRequest(url, {
			body: JSON.stringify({ accessToken }),
			headers: {
				...this.BASE_HEADERS,
				'Content-Type': 'application/json',
			},
		});
	}

	getEntitiesPage(): Promise<ThirdPartyEntityPage> {
		return Promise.reject(new Error('getEntitiesPage method is not implemented in Monday'));
	}

	searchEntities(): Promise<ThirdPartyEntity[]> {
		return Promise.reject(new Error('searchEntities method is not implemented in Monday'));
	}

	getSpaces(): Promise<ThirdPartyEntity[]> {
		return Promise.reject(new Error('getSpaces method is not implemented in Monday'));
	}

	getMultiEntityStats(): Promise<ThirdPartyEntityStats> {
		return Promise.reject(new Error('getMultiEntityStats method is not implemented in Monday'));
	}

	getMultiEntityStatuses(): Promise<ThirdPartyEntityStatuses> {
		return Promise.reject(new Error('getMultiEntityStatuses method is not implemented in Monday'));
	}
}
